import { useState, useEffect, useCallback, useRef, Suspense } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useEnterpriseTracking } from '@/shared/hooks/use-enterprise-tracking'
import { NEWSLETTERS, newsletterStyles } from '../newsletters'

export function HomePage() {
  useDocumentTitle("Inicio")
  const [currentIndex, setCurrentIndex] = useState(NEWSLETTERS.length - 1)
  const { trackVisita, trackScroll, trackClic } = useEnterpriseTracking()
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  const current = NEWSLETTERS[currentIndex]
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < NEWSLETTERS.length - 1

  // Track visita al montar
  useEffect(() => {
    trackVisita(current.key)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Track cambio de newsletter
  const handlePrev = useCallback(() => {
    setCurrentIndex(i => {
      const next = i - 1
      trackVisita(NEWSLETTERS[next].key)
      return next
    })
  }, [trackVisita])

  const handleNext = useCallback(() => {
    setCurrentIndex(i => {
      const next = i + 1
      trackVisita(NEWSLETTERS[next].key)
      return next
    })
  }, [trackVisita])

  // Track scroll con debounce
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
      scrollTimerRef.current = setTimeout(() => {
        const depth = Math.round(
          (container.scrollTop / (container.scrollHeight - container.clientHeight)) * 100
        )
        trackScroll(current.key, depth)
      }, 2000)
    }

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    }
  }, [current.key, trackScroll])

  // Track clics en enlaces (event delegation)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a')
      if (anchor && anchor.href) {
        trackClic(current.key, anchor.href, anchor.textContent?.trim() || '')
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [current.key, trackClic])

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-auto bg-gray-100 dark:bg-gray-900 px-6 py-4">
      {/* Navegación entre newsletters */}
      <div className="flex items-center justify-center py-4 mb-2">
        <div className="inline-flex items-center gap-1 bg-white dark:bg-gray-800 rounded-full px-1.5 py-1 shadow-sm">
          <button
            onClick={handlePrev}
            disabled={!hasPrev}
            className="p-1.5 rounded-full hover:bg-white dark:hover:bg-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all shadow-none hover:shadow-sm"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 capitalize px-4 select-none">
            {current.label}
          </span>
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className="p-1.5 rounded-full hover:bg-white dark:hover:bg-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all shadow-none hover:shadow-sm"
          >
            <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* Contenido del newsletter */}
      <div className="newsletter-container max-w-[620px] mx-auto rounded-lg shadow-md overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
        <style>{newsletterStyles}</style>
        <Suspense fallback={<div className="py-20 text-center text-gray-400 text-sm">Cargando newsletter…</div>}>
          {current.render()}
        </Suspense>
      </div>
    </div>
  )
}
